import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWebSocket, EventTypes } from '../context/WebSocketContext';
import usePageTitle from '../hooks/usePageTitle';
import { 
  getRevenueTrend, 
  getSessionHeatmap, 
  getSessionDistribution,
  getKpiSparklines 
} from '../lib/api';
import { formatCurrency, formatEnergy } from '../lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Battery, 
  Activity,
  RefreshCw,
  Calendar,
  Clock,
  MapPin,
  Wifi,
  WifiOff
} from 'lucide-react';

export default function Analytics() {
  usePageTitle('Analiza');
  const formatLek = (amount) => formatCurrency(amount, 'Lek');
  const formatCredits = (amount) => formatCurrency(amount, 'Kredite');
  
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [sparklines, setSparklines] = useState(null);
  
  const { connected: wsConnected, subscribe } = useWebSocket();

  const periodLabelMap = {
    '7d': '7 ditë',
    '30d': '30 ditë',
    '90d': '3 muaj',
    '1y': '1 vit'
  };
  const periodLabel = periodLabelMap[period] || '30 ditë';

  const displayedSparklines = useMemo(() => {
    const trendData = revenueTrend?.data || [];
    const totals = revenueTrend?.totals || {};
    const current = totals.current || {};
    const change = totals.change || {};

    return {
      revenue: {
        value: current.revenue_lek ?? sparklines?.revenue?.value ?? 0,
        change: change.revenue_lek ?? sparklines?.revenue?.change ?? 0,
        sparkline: trendData.length > 0
          ? trendData.map((d) => Number(d?.revenue_lek || 0))
          : (sparklines?.revenue?.sparkline || []),
      },
      energy: {
        value: current.energy_kwh ?? sparklines?.energy?.value ?? 0,
        change: change.energy_kwh ?? sparklines?.energy?.change ?? 0,
        sparkline: trendData.length > 0
          ? trendData.map((d) => Number(d?.energy_kwh || 0))
          : (sparklines?.energy?.sparkline || []),
      },
      creditsUsed: {
        value: current.credits_used_lek ?? sparklines?.credits_used?.value ?? 0,
        change: change.credits_used_lek ?? sparklines?.credits_used?.change ?? 0,
        sparkline: trendData.length > 0
          ? trendData.map((d) => Number(d?.credits_used_lek || 0))
          : (sparklines?.credits_used?.sparkline || []),
      },
      sessions: {
        value: current.sessions ?? sparklines?.sessions?.value ?? 0,
        change: change.sessions ?? sparklines?.sessions?.change ?? 0,
        sparkline: trendData.length > 0
          ? trendData.map((d) => Number(d?.sessions || 0))
          : (sparklines?.sessions?.sparkline || []),
      },
    };
  }, [revenueTrend, sparklines]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [trendRes, heatmapRes, distRes, sparkRes] = await Promise.all([
        getRevenueTrend(period),
        getSessionHeatmap(period),
        getSessionDistribution(period),
        getKpiSparklines(period)
      ]);
      
      setRevenueTrend(trendRes.data);
      setHeatmap(heatmapRes.data);
      setDistribution(distRes.data);
      setSparklines(sparkRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Real-time updates on session events
  useEffect(() => {
    if (!wsConnected || !subscribe) return;
    
    const handleSessionUpdate = () => {
      // Refresh data when session starts/completes
      fetchData();
    };
    
    const unsubStarted = subscribe(EventTypes.SESSION_STARTED, handleSessionUpdate);
    const unsubCompleted = subscribe(EventTypes.SESSION_COMPLETED, handleSessionUpdate);
    
    return () => {
      unsubStarted();
      unsubCompleted();
    };
  }, [wsConnected, subscribe, fetchData]);

  const TrendIndicator = ({ value, suffix = '%' }) => {
    const isPositive = value >= 0;
    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${
        isPositive ? 'text-green-500' : 'text-red-500'
      }`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {isPositive ? '+' : ''}{value}{suffix}
      </span>
    );
  };

  const MiniSparkline = ({ data, color = '#10b981' }) => (
    <ResponsiveContainer width={80} height={30}>
      <LineChart data={data.map((v, i) => ({ v, i }))}>
        <Line 
          type="monotone" 
          dataKey="v" 
          stroke={color} 
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  // Heatmap component
  const HeatmapCell = ({ count, maxValue }) => {
    const intensity = maxValue > 0 ? count / maxValue : 0;
    const bgColor = intensity === 0 
      ? 'bg-zinc-800' 
      : intensity < 0.25 
        ? 'bg-emerald-900/50' 
        : intensity < 0.5 
          ? 'bg-emerald-700/60' 
          : intensity < 0.75 
            ? 'bg-emerald-500/70' 
            : 'bg-emerald-400';
    
    return (
      <div 
        className={`w-6 h-6 rounded-sm ${bgColor} flex items-center justify-center text-[10px] font-medium transition-all hover:scale-110 cursor-pointer`}
        title={`${count} seanca`}
      >
        {count > 0 && count}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="skeleton h-8 w-48" />
          <div className="skeleton h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
        <div className="skeleton h-80 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-outfit">Analiza të Avancuara</h1>
          <p className="text-muted-foreground mt-1">Statistika dhe trende të detajuara</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${
            wsConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'Live' : 'Offline'}
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 ditë</SelectItem>
              <SelectItem value="30d">30 ditë</SelectItem>
              <SelectItem value="90d">3 muaj</SelectItem>
              <SelectItem value="1y">1 vit</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Rifresko
          </Button>
        </div>
      </div>

      {/* KPI Cards with Sparklines */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-card border-border" data-testid="revenue-kpi">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Të Ardhurat (Topup) ({periodLabel})</p>
                <p className="text-2xl font-bold text-foreground font-outfit mt-1">
                  {formatLek(displayedSparklines?.revenue?.value || 0)}
                </p>
                <TrendIndicator value={displayedSparklines?.revenue?.change || 0} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-500" />
                </div>
                {displayedSparklines?.revenue?.sparkline?.length > 0 && (
                  <MiniSparkline data={displayedSparklines.revenue.sparkline} color="#10b981" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="energy-kpi">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Energji ({periodLabel})</p>
                <p className="text-2xl font-bold text-foreground font-outfit mt-1">
                  {formatEnergy(displayedSparklines?.energy?.value || 0)}
                </p>
                <TrendIndicator value={displayedSparklines?.energy?.change || 0} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Battery className="w-5 h-5 text-blue-500" />
                </div>
                {displayedSparklines?.energy?.sparkline?.length > 0 && (
                  <MiniSparkline data={displayedSparklines.energy.sparkline} color="#3b82f6" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="credits-used-kpi">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Kredite të Përdorura ({periodLabel})</p>
                <p className="text-2xl font-bold text-foreground font-outfit mt-1">
                  {formatCredits(displayedSparklines?.creditsUsed?.value || 0)}
                </p>
                <TrendIndicator value={displayedSparklines?.creditsUsed?.change || 0} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-amber-500" />
                </div>
                {displayedSparklines?.creditsUsed?.sparkline?.length > 0 && (
                  <MiniSparkline data={displayedSparklines.creditsUsed.sparkline} color="#f59e0b" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="sessions-kpi">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Seanca ({periodLabel})</p>
                <p className="text-2xl font-bold text-foreground font-outfit mt-1">
                  {displayedSparklines?.sessions?.value || 0}
                </p>
                <TrendIndicator value={displayedSparklines?.sessions?.change || 0} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-500" />
                </div>
                {displayedSparklines?.sessions?.sparkline?.length > 0 && (
                  <MiniSparkline data={displayedSparklines.sessions.sparkline} color="#a855f7" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue and Energy Trend Charts - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend Chart */}
        <Card className="bg-card border-border" data-testid="revenue-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground font-outfit">Trendi i Të Ardhurave (Topup)</CardTitle>
            <CardDescription>
              Të ardhura nga rimbushje dhe topup
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueTrend?.data || []}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => { 
                    if (!val) return ''; 
                    const parts = val.split('-'); 
                    return parts.length >= 3 ? `${parts[2]}/${parts[1]}` : val.slice(5); 
                  }}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => {
                    if (val >= 1000000) return `${(val/1000000).toFixed(0)}M Lek`;
                    if (val >= 1000) return `${(val/1000).toFixed(0)}k Lek`;
                    return `${val.toLocaleString('sq-AL', {minimumFractionDigits: 0, maximumFractionDigits: 0})} Lek`;
                  }}
                  width={60}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [formatLek(value), 'Të Ardhura (Topup)']}
                  labelFormatter={(label) => { 
                    if (!label) return ''; 
                    const parts = label.split('-'); 
                    return parts.length >= 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : label; 
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue_lek" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  fill="url(#revenueGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Energy Trend Chart */}
        <Card className="bg-card border-border" data-testid="energy-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground font-outfit">Trendi i Energjisë</CardTitle>
            <CardDescription>
              Konsumi ditor i energjisë në kWh
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueTrend?.data || []}>
                <defs>
                  <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => { 
                    if (!val) return ''; 
                    const parts = val.split('-'); 
                    return parts.length >= 3 ? `${parts[2]}/${parts[1]}` : val.slice(5); 
                  }}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => {
                    if (val >= 1000) return `${(val/1000).toFixed(0)}k kWh`;
                    return `${val.toLocaleString('sq-AL', {minimumFractionDigits: 0, maximumFractionDigits: 0})} kWh`;
                  }}
                  width={70}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${Number(value).toLocaleString('sq-AL', {minimumFractionDigits: 2, maximumFractionDigits: 2})} kWh`, 'Energji']}
                  labelFormatter={(label) => { 
                    if (!label) return ''; 
                    const parts = label.split('-'); 
                    return parts.length >= 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : label; 
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="energy_kwh" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  fill="url(#energyGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Session Heatmap - Full Width */}
      <Card className="bg-card border-border" data-testid="session-heatmap">
        <CardHeader>
          <CardTitle className="text-foreground font-outfit flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Harta e Nxehtësisë së Seancave
          </CardTitle>
          <CardDescription>
            {periodLabel} - Oraret më të ngarkuara: {heatmap?.peak_times?.map(p => `${p.day} ora ${p.hour}`).join(', ') || 'Pa të dhëna'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Hour labels */}
              <div className="flex gap-1 mb-2 ml-24">
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} className="w-6 text-center text-[10px] text-muted-foreground">
                    {i % 3 === 0 ? `${i}:00` : ''}
                  </div>
                ))}
              </div>
              
              {/* Heatmap rows */}
              {['E Hënë', 'E Martë', 'E Mërkurë', 'E Enjte', 'E Premte', 'E Shtunë', 'E Diel'].map((day, dayIdx) => (
                <div key={day} className="flex items-center gap-1 mb-1">
                  <div className="w-24 text-sm text-muted-foreground text-right pr-2">{day}</div>
                  <div className="flex gap-1">
                    {Array.from({ length: 24 }, (_, hour) => {
                      const cellData = heatmap?.heatmap?.find(
                        h => h.day_index === dayIdx && h.hour === hour
                      );
                      return (
                        <HeatmapCell 
                          key={hour} 
                          count={cellData?.count || 0} 
                          maxValue={heatmap?.max_value || 1}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 ml-24">
                <span className="text-xs text-muted-foreground">Më pak</span>
                <div className="flex gap-1">
                  <div className="w-4 h-4 rounded-sm bg-zinc-800" />
                  <div className="w-4 h-4 rounded-sm bg-emerald-900/50" />
                  <div className="w-4 h-4 rounded-sm bg-emerald-700/60" />
                  <div className="w-4 h-4 rounded-sm bg-emerald-500/70" />
                  <div className="w-4 h-4 rounded-sm bg-emerald-400" />
                </div>
                <span className="text-xs text-muted-foreground">Më shumë</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Status Distribution */}
        <Card className="bg-card border-border" data-testid="status-distribution">
          <CardHeader>
            <CardTitle className="text-foreground font-outfit">Shpërndarja sipas Statusit</CardTitle>
            <CardDescription>
              Total: {distribution?.total || 0} seanca
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={distribution?.by_status || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="status"
                  label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                >
                  {(distribution?.by_status || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {(distribution?.by_status || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">{item.status}: {item.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Sites by Sessions */}
        <Card className="bg-card border-border" data-testid="top-sites">
          <CardHeader>
            <CardTitle className="text-foreground font-outfit">Stacionet Kryesore</CardTitle>
            <CardDescription>
              Sipas numrit të seancave
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={distribution?.by_site?.slice(0, 5) || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9ca3af" />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#9ca3af" 
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => {
                    if (name === 'count') return [value, 'Seanca'];
                    if (name === 'energy') return [formatEnergy(value), 'Energji'];
                    if (name === 'revenue') return [formatLek(value), 'Të Ardhura (Topup)'];
                    return [value, name];
                  }}
                />
                <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
